<template lang="pug">
  aiq-dialog(:model-value="true"
             width="520px"
             :title="$t(`${I18N_PREFIX}.add.title`)"
             :close-on-click-modal="false"
             :before-close="close")
    .add-webhook
      .add-webhook__field
        label.add-webhook__label {{ $t(`${I18N_PREFIX}.add.webhook`) }}
        aiq-select.add-webhook__select(v-model="webhookId"
                                       size="small"
                                       filterable
                                       :placeholder="$t(`${I18N_PREFIX}.add.webhook_placeholder`)")
          aiq-option(v-for="webhook in webhooks"
                     :key="webhook.id"
                     :label="webhook.name"
                     :value="webhook.id")

      .add-webhook__field(v-if="selected")
        label.add-webhook__label {{ $t(`${I18N_PREFIX}.add.endpoints`) }}
        .add-webhook__empty(v-if="!selected.endpoints.length")
          | {{ $t(`${I18N_PREFIX}.add.no_endpoints`) }}
        aiq-checkbox-group.add-webhook__endpoints(v-else v-model="endpointIds")
          aiq-checkbox.add-webhook__endpoint(v-for="endpoint in selected.endpoints"
                                             :key="endpoint.id"
                                             :label="endpoint.id")
            span.add-webhook__endpoint-name {{ endpoint.name }}
            span.add-webhook__endpoint-method {{ endpoint.method }}
            span.add-webhook__endpoint-path {{ endpoint.path }}

      .add-webhook__hint(v-if="hasWriteSelection") {{ $t(`${I18N_PREFIX}.add.write_hint`) }}

    template(v-slot:footer)
      .add-webhook__actions
        aiq-button(size="small" @click="close") {{ $t('global.cancel') }}
        aiq-button(type="primary"
                   size="small"
                   :disabled="!canAdd"
                   :loading="saving"
                   @click="submit") {{ $t(`${I18N_PREFIX}.add.confirm`) }}
</template>

<script>
import { READ_ONLY_METHODS } from '@/store/modules/mcpConnectors';

const I18N_PREFIX = 'iq_tools_tab.connectors';

export default {
  name: 'AddWebhookDialog',
  props: {
    // Webhooks that have no connector yet, each with the endpoints it offers.
    webhooks: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add', 'close'],
  data() {
    return {
      I18N_PREFIX,
      webhookId: null,
      endpointIds: [],
    };
  },
  computed: {
    selected() {
      return this.webhooks.find(({ id }) => id === this.webhookId) || null;
    },
    /*
     * A webhook with no endpoints has nothing to expose, so adding it would
     * persist nothing and it would drop back out of the list on reload.
     */
    canAdd() {
      return Boolean(this.selected) && this.endpointIds.length > 0;
    },
    // Write endpoints are created without allow_write, so say up front that
    // they will need it before the AI can call them.
    hasWriteSelection() {
      return this.selectedEndpoints().some(
        endpoint => !READ_ONLY_METHODS.includes(endpoint.method.toLowerCase()),
      );
    },
  },
  watch: {
    // Endpoint ids belong to the webhook they were picked from.
    webhookId() {
      this.endpointIds = [];
    },
  },
  methods: {
    selectedEndpoints() {
      if (!this.selected) {
        return [];
      }
      return this.selected.endpoints.filter(({ id }) => this.endpointIds.includes(id));
    },
    submit() {
      this.$emit('add', { webhook: this.selected, endpoints: this.selectedEndpoints() });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./../../../styles/aiq-variables.scss";

.add-webhook {
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: $aiq-font-family;
}

.add-webhook__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.add-webhook__label {
  color: $aiq-text-subheader;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
}

.add-webhook__select {
  width: 100%;
}

.add-webhook__endpoints {
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #E0E6ED;
  padding: 6px 12px;
}

.add-webhook__endpoint {
  display: flex;
  align-items: center;
  height: 28px;
}

.add-webhook__endpoint-name {
  margin-right: 8px;
  color: $aiq-text-main-column-list-item;
  font-weight: 300;
}

.add-webhook__endpoint-method {
  margin-right: 6px;
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.4px;
}

.add-webhook__endpoint-path {
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 300;
}

.add-webhook__empty {
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.add-webhook__hint {
  padding: 10px 12px;
  background-color: $aiq-bgc-entity-orange;
  border: 1px solid #E0E6ED;
  color: $aiq-text-connection-title;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.add-webhook__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
